import { inject } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { ToastrService } from 'ngx-toastr'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { catchError, exhaustMap, map, of, switchMap } from 'rxjs'

import {
  loadCoupons,
  loadCouponsSuccess,
  loadCouponsFailure,
  createCoupon,
  createCouponSuccess,
  createCouponFailure,
  updateCoupon,
  updateCouponSuccess,
  updateCouponFailure,
  deleteCoupon,
  deleteCouponSuccess,
  deleteCouponFailure,
  loadCouponAnalytics,
  loadCouponAnalyticsSuccess,
  loadCouponAnalyticsFailure
} from './coupon.actions'
import { CouponService } from '../../services/coupon.service'

export const loadCoupons$ = createEffect(
  (actions$ = inject(Actions), couponService = inject(CouponService)) =>
    actions$.pipe(
      ofType(loadCoupons),

      switchMap(action =>
        couponService.getCoupons(action.params, action.page, action.limit).pipe(
          map(res =>
            loadCouponsSuccess({
              coupons: res.coupons,
              totalCount: res.totalCount,
              activeCount: res.activeCount,
              inactiveCount: res.inactiveCount,
              expiredCount: res.expiredCount
            })
          ),

          catchError(error => of(loadCouponsFailure({ error: error.message })))
        )
      )
    ),
  { functional: true }
)

export const createCoupon$ = createEffect(
  (
    actions$ = inject(Actions),
    couponService = inject(CouponService),
    toastr = inject(ToastrService),
    modalService = inject(NgbModal)
  ) =>
    actions$.pipe(
      ofType(createCoupon),

      exhaustMap(({ coupon }) =>
        couponService.addCoupon(coupon).pipe(
          map(created => {
            toastr.success('Coupon created successfully')

            modalService.dismissAll()

            return createCouponSuccess({
              coupon: created
            })
          }),

          catchError(error => {
            toastr.error(error.error.message)

            return of(
              createCouponFailure({
                error
              })
            )
          })
        )
      )
    ),
  { functional: true }
)

export const loadCouponAnalytics$ = createEffect(
  (actions$ = inject(Actions), couponService = inject(CouponService)) =>
    actions$.pipe(
      ofType(loadCouponAnalytics),

      switchMap(() =>
        couponService.getAnalytics().pipe(
          map(analytics => loadCouponAnalyticsSuccess({ analytics })),

          catchError(error => of(loadCouponAnalyticsFailure({ error })))
        )
      )
    ),
  { functional: true }
)

export const updateCoupon$ = createEffect(
  (
    actions$ = inject(Actions),
    couponService = inject(CouponService),
    toastr = inject(ToastrService),
    modalService = inject(NgbModal)
  ) =>
    actions$.pipe(
      ofType(updateCoupon),

      exhaustMap(({ id, coupon }) =>
        couponService.editCoupon(id, coupon).pipe(
          map(updated => {
            toastr.success('Coupon updated successfully')

            modalService.dismissAll()

            return updateCouponSuccess({
              coupon: updated
            })
          }),

          catchError(error => {
            toastr.error(error.error.message)

            return of(updateCouponFailure({ error }))
          })
        )
      )
    ),
  { functional: true }
)

export const deleteCoupon$ = createEffect(
  (
    actions$ = inject(Actions),
    couponService = inject(CouponService),
    toastr = inject(ToastrService)
  ) =>
    actions$.pipe(
      ofType(deleteCoupon),

      exhaustMap(({ id }) =>
        couponService.deleteCoupon(id).pipe(
          map(() => {
            toastr.success('Coupon deleted successfully')

            return deleteCouponSuccess({ id })
          }),

          catchError(error => {
            toastr.error(error.error.message)

            return of(deleteCouponFailure({ error }))
          })
        )
      )
    ),
  { functional: true }
)

export const reloadAfterUpdate$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(updateCouponSuccess),
      map(() =>
        loadCoupons({
          params: '',
          page: 1,
          limit: 10,
          context: 'list'
        })
      )
    ),
  { functional: true }
)

export const couponEffects = {
  loadCoupons$,
  loadCouponAnalytics$,
  createCoupon$,
  updateCoupon$,
  deleteCoupon$,
  reloadAfterUpdate$
}
